import { useCallback, useEffect, useState } from 'react';
import { Button, Descriptions, Empty, Modal, Space, Tag, Typography, message } from 'antd';
import API from '../API/API';

const { Text, Title } = Typography;

const NOT_AVAILABLE = 'Not available for this lead';

const sectionStyle = { marginBottom: 20 };

/**
 * navigator.languages normally arrives as an array, but leads saved before the
 * API sanitizer preserved arrays hold {"0":"en-US","1":"en"} instead. Review
 * Leads written by the phone path keep the raw pre-sanitize payload, so both
 * shapes are still in the database.
 */
function formatLanguages(value) {
  if (Array.isArray(value)) {
    return value.length ? value.join(', ') : '';
  }

  if (value && typeof value === 'object') {
    const values = Object.values(value).filter(item => typeof item === 'string');
    return values.length ? values.join(', ') : '';
  }

  return typeof value === 'string' ? value : '';
}

function formatValue(value) {
  if (value === undefined || value === null || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.length ? value.join(', ') : '-';
  if (typeof value === 'object') return JSON.stringify(value);

  return String(value);
}

function formatDateTime(value) {
  if (!value) return '-';

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
}

function hasAnyValue(source) {
  if (!source || typeof source !== 'object') return false;

  return Object.values(source).some(value => {
    if (value === undefined || value === null || value === '') return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return hasAnyValue(value);
    return true;
  });
}

function renderEmptySection(title) {
  return (
    <div style={sectionStyle}>
      <Title level={5}>{title}</Title>
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={<Text type="secondary">{NOT_AVAILABLE}</Text>}
      />
    </div>
  );
}

function renderBrowserSection(browserInfo) {
  if (!hasAnyValue(browserInfo)) {
    return renderEmptySection('Browser Provided');
  }

  const languages = formatLanguages(browserInfo.languages);

  return (
    <div style={sectionStyle}>
      <Title level={5}>Browser Provided</Title>
      <Descriptions bordered size="small" column={2}>
        <Descriptions.Item label="Time Zone">{formatValue(browserInfo.timeZone)}</Descriptions.Item>
        <Descriptions.Item label="UTC Offset (min)">{formatValue(browserInfo.utcOffset)}</Descriptions.Item>
        <Descriptions.Item label="Language">{formatValue(browserInfo.language)}</Descriptions.Item>
        <Descriptions.Item label="Languages">{languages || '-'}</Descriptions.Item>
        <Descriptions.Item label="Platform">{formatValue(browserInfo.platform)}</Descriptions.Item>
        <Descriptions.Item label="Cookies Enabled">{formatValue(browserInfo.cookieEnabled)}</Descriptions.Item>
        <Descriptions.Item label="Java Enabled">{formatValue(browserInfo.javaEnabled)}</Descriptions.Item>
        <Descriptions.Item label="PDF Viewer Enabled">{formatValue(browserInfo.pdfViewerEnabled)}</Descriptions.Item>
        <Descriptions.Item label="User Agent" span={2}>{formatValue(browserInfo.userAgent)}</Descriptions.Item>
      </Descriptions>
    </div>
  );
}

function renderTimezoneSection(timezoneValidation) {
  if (!hasAnyValue(timezoneValidation)) {
    return renderEmptySection('Timezone Check Result');
  }

  return (
    <div style={sectionStyle}>
      <Title level={5}>Timezone Check Result</Title>
      <Descriptions bordered size="small" column={2}>
        <Descriptions.Item label="Decision">
          {timezoneValidation.decision === 'mismatch' ? (
            <Tag color="orange">{timezoneValidation.reasonCode || 'TIMEZONE_MISMATCH'}</Tag>
          ) : (
            <Tag>{timezoneValidation.decision || '-'}</Tag>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Mode">{formatValue(timezoneValidation.mode)}</Descriptions.Item>
        <Descriptions.Item label="Skip Reason">{formatValue(timezoneValidation.skipReason)}</Descriptions.Item>
        <Descriptions.Item label="Reason Code">{formatValue(timezoneValidation.reasonCode)}</Descriptions.Item>
        <Descriptions.Item label="Browser Timezone">{formatValue(timezoneValidation.browserTimeZone)}</Descriptions.Item>
        <Descriptions.Item label="Is US Timezone">{formatValue(timezoneValidation.isUsTimeZone)}</Descriptions.Item>
        <Descriptions.Item label="Checked At" span={2}>{formatDateTime(timezoneValidation.checkedAt)}</Descriptions.Item>
        {timezoneValidation.reason && (
          <Descriptions.Item label="Detail" span={2}>{timezoneValidation.reason}</Descriptions.Item>
        )}
      </Descriptions>
    </div>
  );
}

/**
 * Shows everything the browser sent, plus the browser-timezone check result,
 * for a single lead.
 *
 * source: 'lead'       -> GET /leads/leadById/:id
 *         'reviewLead' -> GET /review-leads/:id  (ADMIN only, as the route is)
 */
export default function LeadFullDetailModal({ open, onClose, leadId, source = 'lead' }) {
  const [loading, setLoading] = useState(false);
  const [record, setRecord] = useState(null);

  const loadDetails = useCallback(async () => {
    setLoading(true);

    try {
      const response = source === 'reviewLead'
        ? await API.getReviewLeadById(leadId)
        : await API.GetLeadById(leadId);

      if (response?.status) {
        setRecord(response.data);
      } else {
        setRecord(null);
        message.error(response?.message || 'Failed to load lead details.');
      }
    } catch (error) {
      console.error('Failed to load lead details:', error);
      setRecord(null);
      message.error('Failed to load lead details.');
    } finally {
      setLoading(false);
    }
  }, [leadId, source]);

  useEffect(() => {
    if (!open || !leadId) {
      return;
    }

    setRecord(null);
    loadDetails();
  }, [open, leadId, loadDetails]);

  // Review Leads written by the phone-validation path keep the original
  // submission under submittedPayload, so fall back to it when the top-level
  // copy is absent.
  const submittedPayload = record?.submittedPayload || {};
  const browserInfo = hasAnyValue(record?.browserInfo) ? record.browserInfo : submittedPayload.browserInfo;
  const timezoneValidation = hasAnyValue(record?.timezoneValidation)
    ? record.timezoneValidation
    : submittedPayload.timezoneValidation;

  return (
    <Modal
      title="Lead Full Detail"
      open={open}
      onCancel={onClose}
      footer={(<Button onClick={onClose}>Close</Button>)}
      width={860}
    >
      {loading ? (
        <Empty description="Loading details..." />
      ) : record ? (
        <Space direction="vertical" size={0} style={{ width: '100%' }}>
          {renderBrowserSection(browserInfo)}
          {renderTimezoneSection(timezoneValidation)}
        </Space>
      ) : (
        <Empty description="No details available" />
      )}
    </Modal>
  );
}
